import React, { useState, useEffect } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Table
} from 'reactstrap';
import {
    Search,
    AlertCircle,
    Terminal,
    Database,
    Code,
    Shield,
    Clock,
    Edit,
    Trash2,
    Plus,
    User,
    Calendar,
    Filter,
    X
} from 'lucide-react';
import axiosInstance from '../../Utils/axiosInstance';
import { useParams } from 'react-router-dom';
import BackButton from '../../Components/BackButton/Index';

function AuditLogDetails() {
    interface ApiLogEntry {
        _id: string;
        user: string | null;
        model: string;
        action: 'created' | 'updated' | 'deleted';
        method: string;
        path: string;
        data?: any;
        old_data?: any;
        created_data?: any;
        changes?: {
            [key: string]: {
                from: string;
                to: string;
            };
        };
    }

    interface TimelineEvent {
        id: string;
        type: 'created' | 'edited' | 'deleted';
        dataType: string;
        dataId: string;
        dataName: string;
        timestamp: Date;
        user: string;
        changes?: {
            field: string;
            oldValue: string;
            newValue: string;
        }[];
        description?: string;
        fullData?: any;
    }

    const [timelineEvents, setTimelineEvents] = useState<TimelineEvent[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // Transform API data to TimelineEvent format
    const transformApiData = (apiData: ApiLogEntry[]): TimelineEvent[] => {
        return apiData.map(log => {
            const dataName = log.data?.name || log.created_data?.name || 'Unknown';
            const dataId = log.path.split('/').find(segment => segment.includes('-')) || log._id;

            let changes: TimelineEvent['changes'] = undefined;
            if (log.changes) {
                changes = Object.entries(log.changes).map(([field, change]) => ({
                    field,
                    oldValue: change.from,
                    newValue: change.to
                }));
            }

            let fullData: any = {};
            if (log.action === 'created') {
                fullData = log.created_data || {};
            } else if (log.action === 'updated') {
                fullData = { ...log.old_data };
                // Add before/after for changed fields
                if (log.changes) {
                    Object.entries(log.changes).forEach(([field, change]) => {
                        fullData[field] = {
                            before: change.from,
                            after: change.to
                        };
                    });
                }
            } else if (log.action === 'deleted') {
                fullData = log.data || {};
            }

            return {
                id: log._id,
                type: log.action === 'updated' ? 'edited' : log.action,
                dataType: log.model,
                dataId,
                dataName,
                timestamp: new Date(), // You might want to add timestamp from your API
                user: log.user || 'System',
                changes,
                description: `${log.action} ${log.model}`,
                fullData
            };
        });
    };
    const { name, id } = useParams();
    useEffect(() => {
        console.log(name)
        console.log(id)

        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                // Replace this with your actual API call
                const response = await axiosInstance(`${process.env.REACT_APP_BACKEND_URL}/paper-app/audit-history-log-details?model=${name}&id=${id}`);

                if (response.status === 200) {

                    const transformedData = transformApiData(response.data.logs);
                    setTimelineEvents(transformedData);
                } else {
                    throw new Error('Failed to fetch audit logs');
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : 'An error occurred');
                console.error('Error fetching audit logs:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Fallback mock data for development
    const mockData: TimelineEvent[] = [];

    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState<'all' | 'created' | 'edited' | 'deleted'>('all');
    const [filterDataType, setFilterDataType] = useState<string>('all');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<TimelineEvent | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    // Use API data if available, otherwise use mock data
    const currentEvents = timelineEvents.length > 0 ? timelineEvents : mockData;

    const filteredEvents = currentEvents.filter(event => {
        const matchesSearch = event.dataName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            event.user.toLowerCase().includes(searchTerm.toLowerCase()) ||
            event.dataId.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesType = filterType === 'all' || event.type === filterType;
        const matchesDataType = filterDataType === 'all' || event.dataType === filterDataType;

        return matchesSearch && matchesType && matchesDataType;
    });

    const sortedEvents = [...filteredEvents].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

    const formatTimestamp = (date: Date) => {
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleCardClick = (event: TimelineEvent) => {
        setSelectedEvent(event);
        setIsModalOpen(true);
        setIsLoading(true);

        // Simulate loading delay
        setTimeout(() => {
            setIsLoading(false);
        }, 1000);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedEvent(null);
        setIsLoading(false);
    };

    const ShimmerLoader = () => (
        <div className="shimmer-container">
            <div className="shimmer-line shimmer-title mb-3"></div>
            <div className="shimmer-line shimmer-subtitle mb-4"></div>
            <div className="shimmer-line shimmer-content mb-2"></div>
            <div className="shimmer-line shimmer-content mb-2"></div>
            <div className="shimmer-line shimmer-content mb-2"></div>
            <div className="shimmer-line shimmer-content-short mb-2"></div>
        </div>
    );

    const renderModalContent = () => {
        if (!selectedEvent) return null;

        if (selectedEvent.type === 'created' || selectedEvent.type === 'deleted') {
            return (
                <div>
                    <h5 className="mb-3">
                        {selectedEvent.type === 'created' ? 'Created Data' : 'Deleted Data'}
                    </h5>
                    <Table striped bordered hover>
                        <tbody>
                            {selectedEvent.fullData && Object.entries(selectedEvent.fullData).map(([key, value]) => (
                                <tr key={key}>
                                    <td className="fw-bold" style={{ width: '40%' }}>{key}</td>
                                    <td>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            );
        }

        if (selectedEvent.type === 'edited') {
            return (
                <div>
                    <h5 className="mb-3">Edit Details</h5>
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th style={{ width: '30%' }}>Field</th>
                                <th style={{ width: '35%' }}>Before</th>
                                <th style={{ width: '35%' }}>After</th>
                            </tr>
                        </thead>
                        <tbody>
                            {selectedEvent.fullData && Object.entries(selectedEvent.fullData).map(([key, value]) => {
                                if (typeof value === 'object' && value !== null && 'before' in value && 'after' in value) {
                                    return (
                                        <tr key={key}>
                                            <td className="fw-bold">{key}</td>
                                            <td className="text-danger text-decoration-line-through">{String(value.before)}</td>
                                            <td className="text-success fw-semibold">{String(value.after)}</td>
                                        </tr>
                                    );
                                } else {
                                    return (
                                        <tr key={key}>
                                            <td className="fw-bold">{key}</td>
                                            <td colSpan={2}>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</td>
                                        </tr>
                                    );
                                }
                            })}
                        </tbody>
                    </Table>
                </div>
            );
        }
    };

    return (
        <div className="page-body bg-gray-900 text-cyan-50 min-vh-100">
            <div className="py-5">
                <Container fluid>
                    <Row>
                        <Col sm="12">
                            <Card className="border-0 shadow-lg bg-dark bg-opacity-75">
                                <CardHeader className="bg-gray-900 border-bottom border-cyan-500">
                                    <div className="card shadow-sm rounded-3 p-4 mb-4">
                                        <BackButton />
                                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
                                            <div>
                                                <h3 className="fw-bold mb-1">Data Timeline</h3>
                                                <p className="text-muted mb-0">Track all changes and activities across your data</p>
                                            </div>
                                            <div className="d-flex align-items-center gap-2 text-muted small mt-3 mt-md-0">
                                                <Calendar size={16} className="me-1" />
                                                <span>Last updated: {formatTimestamp(new Date())}</span>
                                            </div>
                                        </div>
                                        <div className="row g-2">
                                            <div className="col-md-6 position-relative">
                                                <span className="position-absolute top-50 start-0 translate-middle-y ps-3 text-muted">
                                                    <Search size={16} />
                                                </span>
                                                <input
                                                    type="text"
                                                    className="form-control ps-5"
                                                    placeholder="Search by name, user, or ID..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                />
                                            </div>
                                            <div className="col-md-3 d-flex align-items-center gap-2">
                                                <Filter size={16} className="text-muted" />
                                                <select className="form-select" value={filterType} onChange={(e) => setFilterType(e.target.value as any)}>
                                                    <option value="all">All Events</option>
                                                    <option value="created">Created</option>
                                                    <option value="edited">Edited</option>
                                                    <option value="deleted">Deleted</option>
                                                </select>
                                            </div>
                                            <div className="col-md-3">
                                                <select
                                                    className="form-select"
                                                    value={filterDataType}
                                                    onChange={(e) => setFilterDataType(e.target.value)}>
                                                    <option value="all">All Types</option>
                                                    {Array.from(new Set(currentEvents.map(e => e.dataType))).map(type => (
                                                        <option key={type} value={type}>{type}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardBody className="bg-dark text-white">
                                    {loading ? (
                                        <div className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <p className="mt-3 text-muted">Loading audit logs...</p>
                                        </div>
                                    ) : error ? (
                                        <div className="text-center py-5">
                                            <AlertCircle size={32} className="mb-3 text-danger" />
                                            <p className="text-danger">Error: {error}</p>
                                            <Button color="primary" onClick={() => window.location.reload()}>
                                                Retry
                                            </Button>
                                        </div>
                                    ) : sortedEvents.length === 0 ? (
                                        <div className="text-center py-5 text-muted">
                                            <Clock size={32} className="mb-3" />
                                            No events found matching your filters.
                                        </div>
                                    ) : (
                                        sortedEvents.map((event) => (
                                            <div
                                                key={event.id}
                                                className={`event-card bg-white text-dark rounded p-3 mb-3 position-relative border-start border-5 ${event.type === 'created'
                                                    ? 'border-success'
                                                    : event.type === 'edited'
                                                        ? 'border-primary'
                                                        : 'border-danger'
                                                    }`}
                                                onClick={() => handleCardClick(event)}
                                            >
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div>
                                                        <div className="d-flex gap-2 flex-wrap mb-2">
                                                            <span className={`badge ${event.type === 'created'
                                                                ? 'bg-success'
                                                                : event.type === 'edited'
                                                                    ? 'bg-primary'
                                                                    : 'bg-danger'
                                                                } text-capitalize`}>
                                                                {event.type}
                                                            </span>
                                                            <span className="badge bg-secondary">{event.dataType}</span>
                                                        </div>
                                                        {/* <h6 className="fw-bold mb-1">{event.dataName}</h6> */}
                                                        <h6 className="fw-bold mb-1"> Name: {event.user}</h6>
                                                        <div className="text-muted small mb-1"><div>{formatTimestamp(event.timestamp)}</div></div>
                                                    </div>

                                                    <div className="text-muted text-end small">
                                                        <div>{formatTimestamp(event.timestamp)}</div>
                                                        <div><User size={12} className="me-1" /> {event.user}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>

            {/* Modal */}
            <Modal isOpen={isModalOpen} toggle={closeModal} size="lg">
                <ModalHeader toggle={closeModal}>
                    {selectedEvent && (
                        <span className="d-flex align-items-center gap-2">
                            <span className={`badge ${selectedEvent.type === 'created'
                                ? 'bg-success'
                                : selectedEvent.type === 'edited'
                                    ? 'bg-primary'
                                    : 'bg-danger'
                                } text-capitalize`}>
                                {selectedEvent.type}
                            </span>
                            {selectedEvent.dataName}
                        </span>
                    )}
                </ModalHeader>
                <ModalBody>
                    {isLoading ? (
                        <ShimmerLoader />
                    ) : (
                        renderModalContent()
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={closeModal}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>

            <style>
                {`
                .event-card {
                    transition: box-shadow 0.3s ease, transform 0.3s ease;
                    cursor: pointer;
                }
                .event-card:hover {
                    box-shadow: 0 0 10px rgba(0,0,0,0.15);
                    transform: scale(1.02);
                }
                .event-card:active {
                    transform: scale(0.98);
                }
                
                .shimmer-container {
                    padding: 20px;
                }
                
                .shimmer-line {
                    height: 20px;
                    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
                    background-size: 200% 100%;
                    animation: shimmer 1.5s infinite;
                    border-radius: 4px;
                }
                
                .shimmer-title {
                    height: 24px;
                    width: 60%;
                }
                
                .shimmer-subtitle {
                    height: 16px;
                    width: 40%;
                }
                
                .shimmer-content {
                    height: 16px;
                    width: 100%;
                }
                
                .shimmer-content-short {
                    height: 16px;
                    width: 70%;
                }
                
                @keyframes shimmer {
                    0% {
                        background-position: -200% 0;
                    }
                    100% {
                        background-position: 200% 0;
                    }
                }
                `}
            </style>
        </div>
    );
}

export default AuditLogDetails;